import { ProjectCollection } from '@/api';

export function getDefaultNavigationState() {
  return {
    projects: [],
    selectedItem: null,
    selectedItemType: null,
    selectedProject: null,
    all: true,
    searchString: '',
    sortField: 'name',
    sortOrder: null,
    projectCurrentPage: 1,
    projectPerPage: 20,
    totalProjects: 0,
  };
}

export const navigationMutations = {
  SET_PROJECTS(state, projects) {
    state.projects = projects;
  },
  SET_SELECTED_ITEM(state, { item, type }) {
    state.selectedItem = item;
    state.selectedItemType = type;
    if (type === 'project') {
      state.selectedProject = item;
    }
  },
  SET_SELECTED_PROJECT(state, project) {
    state.selectedProject = project;
  },
  SET_ALL_FILTER(state, all) {
    state.all = all;
  },
  SET_SEARCH_STRING(state, searchString) {
    state.searchString = searchString;
  },
  SET_SORT_ORDER(state, order) {
    state.sortOrder = order;
  },
  ADD_IMAGE_GROUP_TO_PROJECT(state, { project, imageGroup }) {
    const proj = state.projects.find(p => p.id === project.id);
    if (proj) {
      proj.imageGroups.push(imageGroup);
      proj.isExpanded = true;
    }
  },
  EXPAND_PROJECT(state, projectToExpand) {
    const project = state.projects.find(p => p.id === projectToExpand.id);
    if (project) {
      project.isExpanded = true;
    }
  },
  TOGGLE_PROJECT_EXPANSION(state, project) {
    const proj = state.projects.find(p => p.id === project.id);
    if (proj) {
      proj.isExpanded = !proj.isExpanded;
    }
  },
  SET_PROJECT_CURRENT_PAGE(state, page) {
    state.projectCurrentPage = page;
  },
  SET_PROJECT_PER_PAGE(state, perPage) {
    state.projectPerPage = perPage;
  },
  SET_TOTAL_PROJECTS(state, total) {
    state.totalProjects = total;
  },
};

export const navigationActions = {
  async fetchProjects({ state, commit }) {
    try {
      const expandedProjectIds = state.projects
        .filter(p => p.isExpanded)
        .map(p => p.id);

      const collectionParams = {
        all: state.all,
        withImageGroups: true,
        nbPerPage: state.projectPerPage
      };

      if (state.searchString && state.searchString.trim() !== '') {
        collectionParams.name = {
          ilike: encodeURIComponent(state.searchString.trim())
        };
      }

      if (state.sortOrder) {
        collectionParams.sort = state.sortField;
        collectionParams.order = state.sortOrder;
      }

      const projectCollection = new ProjectCollection(collectionParams);
      await projectCollection.fetchPage(state.projectCurrentPage - 1);
      const initialProjects = projectCollection.array;
      commit('SET_TOTAL_PROJECTS', projectCollection.totalNbItems);

      const newProjects = initialProjects.map(p => ({
        ...p,
        imageGroups: p.imageGroups || [],
        isExpanded: expandedProjectIds.includes(p.id)
      }));

      commit('SET_PROJECTS', newProjects);

      if (newProjects.length > 0 && !state.selectedItem) {
        commit('SET_SELECTED_ITEM', { item: newProjects[0], type: 'project' });
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  },

  setAllFilter({ commit, dispatch }, all) {
    commit('SET_ALL_FILTER', all);
    return dispatch('fetchProjects');
  },

  setSortOrder({ commit, dispatch }, order) {
    commit('SET_SORT_ORDER', order);
    return dispatch('fetchProjects');
  },

  async toggleProject({ commit, state }, project) {
    const proj = state.projects.find(p => p.id === project.id);
    if (proj) {
      commit('TOGGLE_PROJECT_EXPANSION', project);
    }
    commit('SET_CURRENT_PAGE', 1);
    commit('SET_SELECTED_ITEM', { item: project, type: 'project' });
  },

  async selectImageGroup({ commit, state }, imageGroup) {
    const projectId = imageGroup.project;
    const project = state.projects.find(p => p.id === projectId);
    if (project && !project.isExpanded) {
      commit('EXPAND_PROJECT', project);
    }
    commit('SET_CURRENT_PAGE', 1);
    commit('SET_SELECTED_ITEM', { item: imageGroup, type: 'imageGroup' });
  },

  findProjectForImageGroup({ state }, imageGroup) {
    const projectId = imageGroup.project;
    return state.projects.find(p => p.id === projectId);
  },

  setProjectPage({ commit, dispatch }, page) {
    commit('SET_PROJECT_CURRENT_PAGE', page);
    return dispatch('fetchProjects');
  },

  setProjectPerPage({ commit, dispatch }, perPage) {
    commit('SET_PROJECT_PER_PAGE', perPage);
    commit('SET_PROJECT_CURRENT_PAGE', 1);
    return dispatch('fetchProjects');
  },
};

export const navigationGetters = {
  filteredProjects: state => state.projects,
  getFilteredImageGroups: state => imageGroups => {
    if (!state.searchString || state.searchString.trim() === '') {
      return imageGroups;
    }

    const searchLower = state.searchString.toLowerCase().trim();
    return imageGroups.filter(ig =>
      ig.name.toLowerCase().includes(searchLower)
    );
  },
  projectCurrentPage: state => state.projectCurrentPage,
  projectPerPage: state => state.projectPerPage,
  totalProjects: state => state.totalProjects,
  findProjectForImageGroup: state => imageGroup => {
    const projectId = imageGroup.project;
    return state.projects.find(p => p.id === projectId);
  }
};
